const debug = require('debug')('mtk:order');
import React, { Fragment, useMemo, useEffect, useState, useRef } from 'react';
import ReactToPrint from 'react-to-print';
import Case from 'case';
import classnames from 'classnames';
import moment from 'moment';
import { Map as ImmutableMap } from 'immutable';

import { FiX, FiRefreshCw, FiAlertTriangle as Triangle, FiCheck, FiMail } from 'react-icons/fi';

import { can } from '../can';
import api from '../utils/api';
import { useForm } from '../utils/form';
import { routes } from '../routes';
import { roles, roleLabels, orderTypes, orderParams, userPreferenceOptions } from '../constants';

import { timeout, timeAgo, timeTo, formatDateTime, codeInputRegex, getDefaultLocation } from '../utils';

import { Loading, NotFound } from './PageStatus';
import { List, Body, Row, Cell } from '../components/common/List';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Link from '../components/common/Link';
import Input, { InputRow } from '../components/common/Input';
import Toggle from '../components/common/Toggle';
import Select, { indexToOptions } from '../components/common/Select';
import Radio from '../components/common/Radio';
import OrderLifecycle from '../components/OrderLifecycle';
import OrderInfo from '../components/OrderInfo';
import OrderActivity from '../components/OrderActivity';
import Barcode from '../components/common/Barcode';

import grid from '../styles/grid.css';
import { getProperty } from 'dot-prop';
import css from './Order.css';

let baseOrderFields = {
  locationId: ['number'],
  courierId: ['number'],
  pickupName: ['text', true],
  pickupEmail: ['email', true],
  pickupPhone: ['tel'],
  pickupNationalId: ['rut'],
  width: ['number', true],
  length: ['number', true],
  courierPickupCode: ['text'],
  height: ['number', true],
  preferences: {
    autoReversalEnabled: ['toggle'],
    autoReversalHours: ['number'],
    autoReversalSkipSundays: ['toggle'],
    autoCancelReturnEnabled: ['toggle'],
    autoCancelReturnHours: ['number'],
  },
};

const Order = ({ match, history }) => {
  const { id } = match.params;
  const { user } = api.auth();
  const defaultLocation = getDefaultLocation();

  const { item, actions } = api.orders.item(id, {}, { history });
  const { item: lockerItem, actions: lockerActions } = api.lockers.item(null, null, { lazy: true });
  const { actions: usersActions } = api.users.index(null, { lazy: true });

  const { order, clientId, clientPreferences, lockersAvailable } = useMemo(() => {
    return {
      order: item.loaded && item.data ? item.data.toJS() : { preferences: {} },
      clientId: item.loaded && item.data ? item.data.get('clientId') : null,
      clientPreferences: item.clientPreferences ? item.clientPreferences.toJS() : {},
      lockersAvailable: item.lockersAvailable ? item.lockersAvailable.toJS() : false,
    };
  }, [item.loaded, item]);

  // whether any comm channel (email/sms/whatsapp) is enabled for a given notification type
  const notificationChannelsEnabled = useMemo(
    () => ({
      courierAssigned: !!(clientPreferences.sendCourierAssignedEmail || clientPreferences.sendCourierAssignedSms),
      pickupReady: !!(
        clientPreferences.sendPickupReadyEmail ||
        clientPreferences.sendPickupReadySms ||
        clientPreferences.sendPickupReadyWhatsApp
      ),
      pickupCompleted: !!(
        clientPreferences.sendPickupCompletedEmail ||
        clientPreferences.sendPickupCompletedSms ||
        clientPreferences.sendPickupCompletedWhatsApp
      ),
      pickupReversal: !!(clientPreferences.sendPickupReversalEmail || clientPreferences.sendPickupReversalSms),
      courierReversal: !!(clientPreferences.sendCourierReversalEmail || clientPreferences.sendCourierReversalSms),
      returnReserved: !!(clientPreferences.sendReturnReservedEmail || clientPreferences.sendReturnReservedSms),
      returnReady: !!(clientPreferences.sendReturnReadyEmail || clientPreferences.sendReturnReadySms),
    }),
    [clientPreferences]
  );

  const notificationDisabledTitle = (type) =>
    notificationChannelsEnabled[type] ? undefined : 'Esta notificación no tiene ningún canal (email/sms/whatsapp) habilitado para este cliente';

  // once the order loaded or whenever the reserved locker changes
  // get info on the current locker
  useMemo(() => {
    const lockerId = lockerItem.loaded ? lockerItem.data.get('id') : false;
    if (order.lockerId && lockerId && lockerId !== order.lockerId) {
      lockerActions.get(null, null, order.lockerId);
    }
  }, [order, lockerItem, lockerItem.loaded]);

  const lockerOffline = useMemo(
    () => order.lockerId && lockerItem.loaded && !lockerItem.data.get('online'),
    [order, lockerItem.loaded]
  );

  // Get activity whenever id changes
  useMemo(() => actions.history(), [id]);
  const activity = useMemo(() => (item.history ? item.history.toJS().activity : []), [item.history]);

  // add custom order fields
  const orderFields = useMemo(() => {
    if (!clientPreferences.extras) return baseOrderFields;
    const extras = baseOrderFields.extras || {};
    Object.keys(clientPreferences.extras).forEach((key) => {
      extras[key] = [clientPreferences.extras[key]];
    });
    return { ...baseOrderFields, extras };
  }, [clientPreferences]);

  // initialize form
  const form = useForm(orderFields, item.data, {
    // The form only holds some extras/preferences, so merge them over the stored ones.
    submit: (data) => {
      ['extras', 'preferences'].forEach((key) => {
        const formValue = data.get(key);
        const storedValue = item.data && item.data.get(key);
        if (ImmutableMap.isMap(formValue) && ImmutableMap.isMap(storedValue)) {
          data = data.set(key, storedValue.merge(formValue));
        }
      });
      return actions.update(data);
    },
  });

  useEffect(() => {
    if (clientPreferences.autoReversalEnabled && typeof order.preferences.autoReversalEnabled !== 'boolean') {
      form.setValue(['preferences', 'autoReversalEnabled'], true, true);
    }
  }, [order, clientPreferences]);

  const [state, setState] = useState({ showPickupCode: false, showPrintView: false });
  const [dropCodeType, setDropCodeType] = useState('barcode');
  const [reservingLocker, setReservingLocker] = useState(false);
  const reservingLockerRef = useRef(false);
  const [cancelingLockerReservation, setCancelingLockerReservation] = useState(false);
  const cancelingLockerReservationRef = useRef(false);

  // the error field saves on an explicit button instead of on every keystroke, to avoid
  // triggering the form's throttled autosave while typing
  const [errorDraft, setErrorDraft] = useState(order.error || '');
  const [savingError, setSavingError] = useState(false);
  useEffect(() => {
    setErrorDraft(order.error || '');
  }, [order.error]);
  const saveError = async () => {
    setSavingError(true);
    try {
      await actions.update({ error: errorDraft });
    } finally {
      setSavingError(false);
    }
  };

  const isAdmin = user.role == roles('admin');

  // AUTOREVERSAL

  const {
    autoReversalEnabled,
    autoReversalHours,
    autoReversalSkipSundays,
    autoReversalAt,
    autoReversalAtAgo,
    autoCancelReturnEnabled,
    autoCancelReturnHours,
    autoCancelReturnAt,
  } = useMemo(() => {
    const autoReversalEnabled = form.getValue(['preferences', 'autoReversalEnabled']);
    const formAutoReversalSkipSundays = form.getValue(['preferences', 'autoReversalSkipSundays']);
    const autoReversalSkipSundays =
      typeof formAutoReversalSkipSundays == 'boolean'
        ? formAutoReversalSkipSundays
        : typeof order.preferences.autoReversalSkipSundays == 'boolean'
          ? order.preferences.autoReversalSkipSundays
          : !!clientPreferences.autoReversalSkipSundays;
    const autoReversalHours =
      form.getValue(['preferences', 'autoReversalHours']) ||
      clientPreferences.autoReversalHours ||
      order.preferences.autoReversalHours ||
      72;
    const hasValidReadyAt = order.readyAt && moment(order.readyAt).isValid();
    const autoReversalAt = hasValidReadyAt ? moment(order.readyAt).add(autoReversalHours, 'hours') : null;
    const autoReversalAtAgo = autoReversalAt ? timeAgo(autoReversalAt) : '';
    const autoCancelReturnEnabled = form.getValue(['preferences', 'autoCancelReturnEnabled']);
    const autoCancelReturnHours =
      form.getValue(['preferences', 'autoCancelReturnHours']) ||
      clientPreferences.autoCancelReturnHours ||
      order.preferences.autoCancelReturnHours ||
      72;

    const autoCancelReturnAt = hasValidReadyAt ? moment(order.readyAt).add(autoCancelReturnHours, 'hours') : null;

    return {
      autoReversalEnabled,
      autoReversalHours,
      autoReversalSkipSundays,
      autoReversalAt,
      autoReversalAtAgo,
      autoCancelReturnEnabled,
      autoCancelReturnHours,
      autoCancelReturnAt,
    };
  }, [order, clientPreferences, form]);

  const readOnly = !can(user, 'update', 'orders') || ['completed', 'cancelled'].includes(order.status) || lockerOffline;
  const measuresReadOnly = order.lockerId || readOnly;
  const isReturn = order.orderType == 'return';
  const isReservedOrder = order.status === 'reserved';
  const autoCancelReservedHours = Number(clientPreferences.autoCancelReservedHours);
  const autoDeleteReservedHours = Number(clientPreferences.autoDeleteReservedHours);
  const autoCancelReservedConfigured =
    clientPreferences.autoCancelReservedEnabled === true &&
    Number.isFinite(autoCancelReservedHours) &&
    autoCancelReservedHours >= 1;
  const autoDeleteReservedConfigured =
    clientPreferences.autoDeleteReservedEnabled === true &&
    Number.isFinite(autoDeleteReservedHours) &&
    autoDeleteReservedHours >= 1;
  const showAutoCancelReserved = isReservedOrder && autoCancelReservedConfigured;
  const showAutoDeleteReserved = isReservedOrder && autoDeleteReservedConfigured;
  const autoCancelReservedAt =
    showAutoCancelReserved && order.reservedAt && moment(order.reservedAt).isValid()
      ? moment(order.reservedAt).add(autoCancelReservedHours, 'hours')
      : null;
  const autoDeleteReservedAt =
    showAutoDeleteReserved && order.reservedAt && moment(order.reservedAt).isValid()
      ? moment(order.reservedAt).add(autoDeleteReservedHours, 'hours')
      : null;
  const associatedProducts = Array.isArray(order.extras && order.extras.products) ? order.extras.products : [];
  const isVisibleSetting = (e) => e.value !== false && e.value !== 'false' && e.value != null && e.value !== '';
  const preferenceSettings = userPreferenceOptions
    .filter((o) => order.preferences && typeof order.preferences[o.name] != 'undefined')
    .map((o) => ({ name: o.name, value: order.preferences[o.name] }))
    .filter(isVisibleSetting);
  const extraSettings =
    order.extras && typeof order.extras == 'object'
      ? Object.keys(order.extras)
          .filter((key) => key != 'products')
          .map((key) => ({ name: key, value: order.extras[key] }))
          .filter(isVisibleSetting)
      : [];
  const extraSettingLabel = ({ name, value }) => {
    const option = userPreferenceOptions.find((o) => o.name == name);
    const label = option ? option.label : Case.capital(name);
    if (value === true || value === 'true') return label;
    return `${label}: ${typeof value == 'object' ? JSON.stringify(value) : value}`;
  };
  const removeSetting = (key, name) => {
    const stored = item.data && ImmutableMap.isMap(item.data.get(key)) ? item.data.get(key) : ImmutableMap();
    if (key == 'extras' && clientPreferences.extras && name in clientPreferences.extras) {
      form.setValue(['extras', name], null, true);
    }
    // Sent pre-serialized so the null survives prepareData's null filtering.
    actions.update({ [key]: JSON.stringify(stored.set(name, null).toJS()) });
  };
  const removeSettingButton = (key, name) =>
    readOnly ? null : (
      <button
        type="button"
        className={css.badgeRemove}
        title="Eliminar"
        onClick={() => removeSetting(key, name)}>
        <FiX />
      </button>
    );
  const barcodeRef = useRef();

  // ACTION

  // this is a bit cumbersome but it does the trick.
  // we need to wait for the order to load, in order to correctly load the couriers
  // this contraption continues to run until there is a clientId to use for couriers.
  // the useRef is necessary to make sure the recursive function is using the current
  // version of the function (with the latest clientId)
  const getCouriers = useRef();

  getCouriers.current = async (search, role) => {
    if (!order.clientId) {
      await timeout(200);
      return getCouriers.current(search, '!noAccess');
    }
    return indexToOptions(
      await usersActions.get({ search, role: '!noAccess', status: 'active', clientId: order.clientId, perPage: 50 })
    );
  };
  const findAvailableLockers = () => {
    const values = form.getValues().toJS();
    const clientId = order.clientId;
    const hasLocation =
      order.locationLat != null &&
      order.locationLon != null &&
      String(order.locationLat).trim() !== '' &&
      String(order.locationLon).trim() !== '' &&
      Number.isFinite(Number(order.locationLat)) &&
      Number.isFinite(Number(order.locationLon));
    const location = hasLocation
      ? { lat: Number(order.locationLat), lon: Number(order.locationLon) }
      : defaultLocation;
    actions.lockersFindAvailable({ ...values, ...location, clientId });
  };

  const lockerReserve = async (lockerId) => {
    if (reservingLockerRef.current) return;

    reservingLockerRef.current = true;
    setReservingLocker(true);
    try {
      await actions.lockerReserve(lockerId);
    } finally {
      reservingLockerRef.current = false;
      setReservingLocker(false);
    }
  };

  const lockerUnreserve = async () => {
    if (cancelingLockerReservationRef.current) return;

    cancelingLockerReservationRef.current = true;
    setCancelingLockerReservation(true);
    try {
      await actions.lockerUnreserve(order.lockerId);
    } finally {
      cancelingLockerReservationRef.current = false;
      setCancelingLockerReservation(false);
    }
  };

  const sendNotification = (notificationType) => {
    actions.sendNotification(notificationType);
  };

  const onCancel = async () => {
    await actions.cancel();
  };

  const onDelete = async () => {
    await actions.delete();
  };

  const onReturnFromOrder = async () => {
    await actions.returnFromOrder();
  };

  const togglePickupCode = () => {
    setState((s) => ({ ...s, showPickupCode: !s.showPickupCode }));
  };

  if (item.status == 404) return <NotFound />;
  if (!item.loaded) return <Loading />;

  return (
    <main className={css.root}>
      <PageHeader
        section="orders"
        title={`Orden #${id}`}
        subtitle={
          <Fragment>
            <h3>
              <span className={classnames(css.orderType, [css[order.orderType]])}>{orderTypes(order.orderType)}</span>
              {order.orderType == 'return'
                ? ` de ${order.pickupName} ${isAdmin ? `a ${order.clientName ?? '...'}` : ''}`
                : ` ${isAdmin ? `de ${order.clientName}` : ''} para ${order.pickupName}`}
            </h3>

            {order.returnOrderId && (
              <>
                Orden de devolución <strong>{order.returnOrderId}</strong> &nbsp;&nbsp;&nbsp;
                <Button small to={routes('order', { id: order.returnOrderId })}>
                  Ver orden
                </Button>
              </>
            )}
            {order.fromOrderId && (
              <>
                Creado desde envío <strong>{order.fromOrderId}</strong> &nbsp;&nbsp;&nbsp;
                <Button small to={routes('order', { id: order.fromOrderId })}>
                  Ver orden
                </Button>
              </>
            )}
            {lockerOffline ? (
              <Fragment>
                <Triangle color={'var(--color-red)'} /> <strong>Locker Offline</strong>
                {` Edición temporalmente deshabilitada.`}
              </Fragment>
            ) : null}
          </Fragment>
        }
        dirty={form.dirty}
        autoSave={!lockerOffline}
        {...item}>
        {order.status == 'ready' && order.orderType == 'delivery' && (
          <div style={{ marginBottom: '2rem' }}>
            <p>
              <small>
                {'Paquete en puerta desde '}
                <strong>{formatDateTime(order.readyAt)}</strong> ({timeAgo(order.readyAt)})
              </small>
            </p>
          </div>
        )}
        {can(user, 'delete', 'orders') && ['created', 'reserved'].includes(order.status) && (
          <Button confirm="¿Eliminar esta orden?" destructive onClick={onDelete} disabled={lockerOffline}>
            Eliminar
          </Button>
        )}
        {can(user, 'cancel', 'orders') && ['created', 'reserved'].includes(order.status) && (
          <Button disabled={lockerOffline} confirm="¿Cancelar esta orden?" destructive onClick={onCancel}>
            Cancelar orden
          </Button>
        )}

        {clientPreferences.returnsEnabled &&
          order.status == 'completed' &&
          order.orderType == 'delivery' &&
          !order.returnOrderId && (
            <Button
              confirm="¿Crear una devolución basada en esta orden?"
              onClick={onReturnFromOrder}
              loading={item.loading && item.action == 'create-order'}>
              {item.loading && item.action == 'create-order' ? 'Creando devolución' : 'Crear devolución'}
            </Button>
          )}
      </PageHeader>

      {order.preferences && order.preferences.notificationsDisabled === true && (
        <div className={css.notificationsWarning} role="status">
          <Triangle size={16} />
          <span>No se enviarán notificaciones para esta orden.</span>
        </div>
      )}

      {item.loaded && item.data && <OrderLifecycle {...item.data.toJS()} />}

      <div className={grid.grid}>
        <section className={classnames(grid.half, css.orderDetailsColumn)}>
          <div className={classnames(css.formPanel, css.orderMeasuresPanel)}>
            <h2 className={css.panelTitle}>Medidas del paquete</h2>
            <InputRow>
              {measuresReadOnly ? (
                <div className={css.measurementsSummary}>
                  <div className={css.measurementsValues}>
                    <span>{form.getValue('width')}</span>
                    <span className={css.measurementsTimes}>×</span>
                    <span>{form.getValue('height')}</span>
                    <span className={css.measurementsTimes}>×</span>
                    <span>{form.getValue('length')}</span>
                    <span className={css.measurementsUnit}>cm</span>
                  </div>
                  <div className={css.measurementsCaption}>Ancho × Alto × Largo</div>
                </div>
              ) : (
                <Fragment>
                  <Input className={css.measurementsInput} name="width" form={form} label="Ancho" sufix=" cm" min="1" />
                  <FiX className={css.measurementsIcon} />
                  <Input className={css.measurementsInput} name="height" form={form} label="Alto" sufix=" cm" min="1" />
                  <FiX className={css.measurementsIcon} />
                  <Input className={css.measurementsInput} name="length" form={form} label="Largo" sufix=" cm" min="1" />
                </Fragment>
              )}
            </InputRow>

            {order.status == 'ready' && order.orderType == 'delivery' && (
              <div>
                {can(user, 'reversal', 'orders') && (
                  <Button
                    small
                    className={css.revokeOrderButton}
                    confirm="¿Revocar esta orden?"
                    disabled={readOnly}
                    onClick={actions.reversal}>
                    Revocar orden ahora
                  </Button>
                )}
                
              </div>
            )}
            {order.orderType == 'reversal' && (
              <p className={classnames(css.autoreversalNote)}>
                <small>Esta orden ya fue revocada.</small>
              </p>
            )}
          </div>

          <div
            className={classnames(css.formPanel, {
              [css.formPanelReserved]: order.status === 'reserved' && order.lockerId,
              [css.formPanelReady]: order.status === 'ready' && order.orderType === 'delivery' && order.lockerId,
              [css.formPanelReversalReady]: order.status === 'ready' && order.orderType === 'reversal' && order.lockerId,
              [css.formPanelCompleted]: order.status === 'completed' && order.lockerId,
            })}>
          <h2 className={css.panelTitle}>Locker</h2>
          {order.status === 'created' && order.orderType === 'delivery' && !order.lockerId && (
            <p className={css.lockerNextStep}>Siguiente paso: selecciona un locker para continuar con la entrega.</p>
          )}
          {can(user, 'notification', 'orders') &&
            ['created', 'reserved'].includes(order.status) &&
            order.courierId > 0 &&
            order.orderType == 'delivery' && (
              <div className={css.lockerCourierTip}>
                <FiMail className={css.lockerCourierTipIcon} />
                <span>Notificar al courier asignado que debe retirar el paquete.</span>
                <Button
                  small
                  className={css.resendNotificationButton}
                  confirm={`¿Reenviar notificación de asignación a courier ${order.courierName || order.courierEmail
                    }?`}
                  disabled={!order.lockerId || !notificationChannelsEnabled.courierAssigned}
                  title={notificationDisabledTitle('courierAssigned')}
                  onClick={() => sendNotification('courierAssigned')}>
                  Notificar a courier
                </Button>
                {!order.lockerId && (
                  <small>Solo se pueden enviar notificaciones al courier una vez que se asignó un locker.</small>
                )}
              </div>
            )}
          {!order.lockerId && order.locationId && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                margin: '1rem 0',
              }}>
              <p style={{ fontSize: '1rem' }}>
                Ubicación seleccionada: <br />
                <strong>{order.locationName}</strong>
                <br />
                {order.locationAddress}
              </p>
              {can(user, 'update', 'orders') && (
                <Button small onClick={() => form.setValue('locationId', null)}>
                  Remover preferencia de ubicación
                </Button>
              )}
            </div>
          )}
          {order.lockerId ? (
            <div className={css.lockerAssignedPanel}>
              {can(user, 'update', 'orders') && order.status == 'reserved' && (
                <Button
                  small
                  className={css.cancelReservationCorner}
                  disabled={lockerOffline || cancelingLockerReservation}
                  loading={cancelingLockerReservation}
                  onClick={lockerUnreserve}>
                  {cancelingLockerReservation ? 'Cancelando...' : 'Cancelar reserva'}
                </Button>
              )}
              <span>
                {order.status === 'reserved' && <span className={css.reservedBadge}>Reservado</span>}
                <strong>{order.lockerName}</strong>
              </span>
              {order.status === 'reserved' && order.orderType === 'delivery' && (
                <p className={css.lockerReadyTip}>
                  <FiCheck className={css.lockerReadyTipIcon} />
                  <span>Ya puedes entregar este paquete en el locker con el código de entrega.</span>
                </p>
              )}
              {order.status === 'reserved' && order.orderType === 'return' && (
                <div className={css.lockerReservedReturnTip}>
                  <FiCheck className={css.lockerReservedReturnTipIcon} />
                  <span>Esta puerta está reservada, esperando que se deposite el paquete en el locker.</span>
                  {can(user, 'notification', 'orders') && (
                    <Button
                      small
                      className={css.resendNotificationButton}
                      confirm={`¿Reenviar notificación a ${order.pickupName}?`}
                      disabled={!notificationChannelsEnabled.returnReserved}
                      title={notificationDisabledTitle('returnReserved')}
                      onClick={() => sendNotification('returnReserved')}>
                      <FiMail className={css.resendNotificationIcon} />
                      Reenviar notificación
                    </Button>
                  )}
                </div>
              )}
              {order.status === 'ready' && order.orderType === 'delivery' && (
                <div className={css.lockerPickupTip}>
                  <Triangle className={css.lockerPickupTipIcon} />
                  <span>El paquete está en la puerta esperando ser retirado.</span>
                 
                  {can(user, 'notification', 'orders') && (
                    <Button
                      small
                      className={css.resendNotificationButton}
                      confirm={`¿Reenviar notificación a ${order.pickupName}?`}
                      disabled={!notificationChannelsEnabled.pickupReady}
                      title={notificationDisabledTitle('pickupReady')}
                      onClick={() => sendNotification('pickupReady')}>
                      <FiMail className={css.resendNotificationIcon} />
                      Reenviar notificación de retiro
                    </Button>
                  )}
                </div>
              )}
              {order.status === 'ready' && order.orderType === 'return' && (
                <div className={css.lockerPickupTip}>
                  <Triangle className={css.lockerPickupTipIcon} />
                  <span>El paquete está listo para ser recolectado por el courier.</span>
                  {can(user, 'notification', 'orders') && (
                    <Button
                      small
                      className={css.resendNotificationButton}
                      confirm={`¿Reenviar notificación a courier?`}
                      disabled={!notificationChannelsEnabled.returnReady}
                      title={notificationDisabledTitle('returnReady')}
                      onClick={() => sendNotification('returnReady')}>
                      <FiMail className={css.resendNotificationIcon} />
                      Reenviar notificación a courier
                    </Button>
                  )}
                </div>
              )}
              {order.status === 'ready' && order.orderType === 'reversal' && (
                <div className={css.lockerReversalTip}>
                  <Triangle className={css.lockerReversalTipIcon} />
                  <span>Esta orden fue revocada: el paquete debe ser retirado del locker.</span>
                  {can(user, 'notification', 'orders') && (
                    <div className={css.reversalNotificationActions}>
                      <Button
                        small
                        className={css.resendNotificationButton}
                        confirm={`¿Reenviar notificación a ${order.pickupName}?`}
                        disabled={!notificationChannelsEnabled.pickupReversal}
                        title={notificationDisabledTitle('pickupReversal')}
                        onClick={() => sendNotification('pickupReversal')}>
                        <FiMail className={css.resendNotificationIcon} />
                        Notificar a usuario
                      </Button>
                      <Button
                        small
                        className={css.resendNotificationButton}
                        confirm={`¿Reenviar notificación a ${order.courierName || order.courierEmail}?`}
                        disabled={!notificationChannelsEnabled.courierReversal}
                        title={notificationDisabledTitle('courierReversal')}
                        onClick={() => sendNotification('courierReversal')}>
                        <FiMail className={css.resendNotificationIcon} />
                        Notificar a courier
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {order.status === 'completed' && order.orderType === 'delivery' && (
                <div className={css.lockerCompletedTip}>
                  <FiCheck className={css.lockerCompletedTipIcon} />
                  <span>El paquete fue retirado del locker.</span>
                  {can(user, 'notification', 'orders') && (
                    <Button
                      small
                      className={css.resendNotificationButton}
                      confirm={`¿Reenviar notificación a ${order.pickupName}?`}
                      disabled={!notificationChannelsEnabled.pickupCompleted}
                      title={notificationDisabledTitle('pickupCompleted')}
                      onClick={() => sendNotification('pickupCompleted')}>
                      <FiMail className={css.resendNotificationIcon} />
                      Reenviar notificación
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : lockersAvailable ? (
            <List>
              <Body>
                {lockersAvailable.length == 0 ? (
                  <Row>
                    <Cell>
                      <p>No hay lockers disponibles cerca de esta ubicación</p>
                    </Cell>
                  </Row>
                ) : (
                  lockersAvailable.map((l, index) => (
                    <Row
                      hover={state.hoveredLocker == l.id}
                      key={`locker-${index}`}
                      style={{ verticalAlign: 'top' }}
                      onMouseEnter={() => setState((s) => ({ ...s, hoveredLocker: l.id }))}
                      onMouseLeave={() => setState((s) => ({ ...s, hoveredLocker: null }))}>
                      <Cell top>
                        <h3>{l.name}</h3>
                        <small
                          style={{
                            color: l.doorsAvailable > 3 ? 'var(--color-green)' : 'var(--color-yellow)',
                          }}>
                          <strong>{l.doorsAvailable} disponibles</strong>
                        </small>
                      </Cell>
                      <Cell>
                        <h3>{l.locationName}</h3>
                        <small>{l.locationAddress}</small>
                      </Cell>
                      {can(user, 'update', 'orders') && (
                        <Cell
                          style={{
                            verticalAlign: 'center',
                            paddingLeft: 5,
                          }}>
                          <Button
                            className={css.blueActionButton}
                            small
                            disabled={!l.online || reservingLocker}
                            loading={l.online && reservingLocker}
                            onClick={() => lockerReserve(l.id)}
                            style={{ width: '100%', marginBottom: 5 }}>
                            {!l.online ? 'Locker offfline' : reservingLocker ? 'Reservando...' : 'Reservar locker'}
                          </Button>
                          <Button
                            small
                            style={{ width: '100%' }}
                            onClick={() => form.setValue('locationId', l.locationId)}>
                            Guardar preferencia de ubicación
                          </Button>
                        </Cell>
                      )}
                    </Row>
                  ))
                )}
              </Body>
            </List>
          ) : null}
          {!order.lockerId && (
            <Button
              className={css.findLockersButton}
              disabled={readOnly}
              onClick={findAvailableLockers}>
              {lockersAvailable ? 'Volver a buscar' : 'Buscar lockers disponibles'}
            </Button>
          )}
          </div>

          {order.status !== 'completed' && (
          <div className={css.orderErrorPanel}>
            <details className={css.errorAccordion} open={!!order.error}>
              <summary className={css.errorSummary}>
                Informar un error con la orden <span className={css.errorOptionalBadge}>opcional</span>
              </summary>
              <div className={css.errorDetails}>
                <Input
                  className={classnames({ [css.errorField]: errorDraft })}
                  name="error"
                  label="Error"
                  readOnly={readOnly}
                  value={errorDraft}
                  onChange={(_, value) => setErrorDraft(value)}
                  onEnter={saveError}
                  placeholder="Sin error"
                />
                <Button
                  small
                  className={css.errorSaveButton}
                  disabled={readOnly || savingError || errorDraft === (order.error || '')}
                  loading={savingError}
                  onClick={saveError}>
                  {savingError ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </details>
          </div>
          )}

          {order.orderType === 'delivery' && (
            <div className={classnames(css.formPanel, css.orderAutomationPanel)}>
              <h2 className={css.panelTitle}>Automatizaciones</h2>

              <details className={css.automationPanel}>
                <summary className={css.automationSummary}>Autorevocación</summary>
                <div className={css.automationDetails}>
                  {clientPreferences.autoReversalEnabled === true ? (
                    order.status === 'cancelled' ? (
                      <p>La orden está cancelada; no se ejecutará la autorevocación.</p>
                    ) : (
                      <>
                        <p className={css.autoreversalNote}>
                          La orden {autoReversalEnabled ? null : <strong>NO</strong>} será revocada automáticamente
                          {autoReversalEnabled ? (
                            <>
                              {' '}luego de {autoReversalHours} horas de ser entregada en el locker
                              {order.preferences.autoReversalSkipSundays
                                ? ', se omitirán los domingos en el cálculo del plazo'
                                : null}
                              {autoReversalAt ? (
                                <span style={{ display: 'inline-block' }}>
                                  {' el '}
                                  <strong>{formatDateTime(autoReversalAt, 'dddd D [de] MMM [de] YYYY [a] [las] hh:mma')}</strong>
                                  {autoReversalAtAgo ? ` (${autoReversalAtAgo})` : null}
                                </span>
                              ) : null}
                            </>
                          ) : null}
                          .
                        </p>
                        {can(user, 'reversal', 'orders') && (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Toggle
                              style={{ paddingTop: 7 }}
                              form={form}
                              name={['preferences', 'autoReversalEnabled']}
                              label="Auto-revocación"
                              readOnly={readOnly}
                            />
                            {autoReversalEnabled && (
                              <Input
                                name={['preferences', 'autoReversalHours']}
                                label="Ajustar cantidad de horas para iniciar auto-revocación"
                                placeholder={autoReversalHours}
                                sufix="horas"
                                readOnly={readOnly}
                                form={form}
                              />
                            )}
                          </div>
                        )}
                      </>
                    )
                  ) : (
                    <p>No habilitada</p>
                  )}
                </div>
              </details>

              <details className={css.automationPanel}>
                <summary className={css.automationSummary}>Auto cancelación de reserva</summary>
                <div className={css.automationDetails}>
                  {autoCancelReservedConfigured ? (
                    <>
                      <p>
                        Si la orden no se carga en el locker, la reserva se cancelará automáticamente después de{' '}
                        {autoCancelReservedHours} horas.
                      </p>
                      {autoCancelReservedAt && (
                        <p>
                          Fecha estimada: <strong>{formatDateTime(autoCancelReservedAt)}</strong> ({timeAgo(autoCancelReservedAt)})
                        </p>
                      )}
                    </>
                  ) : (
                    <p>No habilitada</p>
                  )}
                </div>
              </details>

              <details className={css.automationPanel}>
                <summary className={css.automationSummary}>Auto eliminación de reserva</summary>
                <div className={css.automationDetails}>
                  {autoDeleteReservedConfigured ? (
                    <>
                      <p>
                        Si la orden no se carga en el locker, la reserva se eliminará automáticamente después de{' '}
                        {autoDeleteReservedHours} horas.
                      </p>
                      {autoDeleteReservedAt && (
                        <p>
                          Fecha estimada: <strong>{formatDateTime(autoDeleteReservedAt)}</strong> ({timeAgo(autoDeleteReservedAt)})
                        </p>
                      )}
                    </>
                  ) : (
                    <p>No habilitada</p>
                  )}
                </div>
              </details>
            </div>
          )}

          <div className={classnames(css.formPanel, css.orderRecipientPanel)}>
            <h2 className={css.panelTitle}>{isReturn ? 'Remitente' : 'Destinatario'}</h2>
            <Input name="pickupName" form={form} readOnly={readOnly} label="Nombre" />
            <Input name="pickupEmail" form={form} readOnly={readOnly} label="Email" />
            <Input name="pickupPhone" form={form} readOnly={readOnly} label="Teléfono" />
            {clientPreferences.enableUsersNationalId && (
              <Input name="pickupNationalId" form={form} readOnly={readOnly} label="Cédula" />
            )}
          </div>

          {preferenceSettings.length > 0 && (
            <div className={classnames(css.formPanel, css.orderPreferencesPanel)}>
              <h2 className={css.panelTitle}>Ajustes especiales</h2>
              <div className={css.extraBadges}>
                {preferenceSettings.map((pref) =>
                  pref.name == 'enableDisabilityPickup' ? (
                    <span key={pref.name} className={css.disabilityBadge}>
                      Movilidad reducida: requiere puerta accesible
                      {removeSettingButton('preferences', pref.name)}
                    </span>
                  ) : (
                    <span key={pref.name} className={css.extraBadge}>
                      {extraSettingLabel(pref)}
                      {removeSettingButton('preferences', pref.name)}
                    </span>
                  )
                )}
              </div>
            </div>
          )}

          {extraSettings.length > 0 && (
            <div className={classnames(css.formPanel, css.orderSettingsPanel)}>
              <h2 className={css.panelTitle}>Configuración extra</h2>
              <div className={css.extraBadges}>
                {extraSettings.map((extra) => (
                  <span key={extra.name} className={css.extraBadge}>
                    {extraSettingLabel(extra)}
                    {removeSettingButton('extras', extra.name)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className={classnames(css.formPanel, css.orderCourierPanel)}>
            <h2 className={css.panelTitle}>Courier</h2>
            <Select
              name="courierId"
              form={form}
              key="order-courier-select"
              loadOptions={getCouriers.current}
              label="Courier"
              readOnly={readOnly}
              placeholder="No asignado"
              note="Puedes asignar el courier ahora; si lo dejas vacío, se asignará quien cargue la orden en el locker."
            />
          </div>

          {(clientPreferences.extras || clientPreferences.enableProductsMenu) && (
            <div className={classnames(css.formPanel, css.orderAdditionalPanel)}>
              {clientPreferences.extras && (
                <Fragment>
                  <h2 className={css.panelTitle}>Extras</h2>
                  {Object.keys(clientPreferences.extras).map((field, index) => (
                    <Input
                      key={`extras-field-${index}`}
                      name={['extras', field]}
                      readOnly={readOnly}
                      form={form}
                      label={Case.capital(field)}
                    />
                  ))}
                </Fragment>
              )}

              {clientPreferences.enableProductsMenu && (
                <Fragment>
                  <h2 className={css.panelTitle}>Productos Asociados</h2>
                  {associatedProducts.length > 0 ? (
                    <div>
                      {associatedProducts.map((product, index) => (
                        <span key={`order-associated-product-${product.id || index}`} className={css.productTag}>
                          {product.name || 'Producto'}{product.sku ? ` (${product.sku})` : ''}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p>No hay productos asociados</p>
                  )}
                </Fragment>
              )}
            </div>
          )}

        </section>

        <section className={classnames(grid.half)}>
          <div className={classnames(css.formPanel, css.orderCodesPanel)}>
            {isReturn ? (
            <section id={'return-codes'}>
              <h3>Código de entrega (cliente)</h3>
              <p>
                <small>
                  El código de entrega se envía directamente al remitente via email una vez que se hace una reserva de
                  locker.
                </small>
              </p>
        

              <h3>Código de retiro (courier)</h3>
              {form.getValue('courierPickupCode') ? (
                <div className={css.row}>
                  <Barcode className={css.barcode} ref={barcodeRef} code={form.getValue('courierPickupCode')} />
                  <ReactToPrint
                    trigger={() => (
                      <div style={{ textAlign: 'center' }}>
                        <Button className={css.blueActionButton}>Imprimir código</Button>
                        <div>
                          <small>(etiqueta 10 x 5 cm)</small>
                        </div>
                      </div>
                    )}
                    content={() => barcodeRef.current}
                  />
                </div>
              ) : (
                <p>
                  <small>
                    {order.status == 'ready'
                      ? 'El paquete puede ser retirado sin código por cualquier courier autorizado.'
                      : 'El paquete podrá ser retirado sin código por cualquier courier autorizado.'}
                  </small>
                </p>
              )}
              <Input
                name="courierPickupCode"
                form={form}
                beforeChange={(v) => v.replace(codeInputRegex, '')}
                placeholder="Sin código"
                readOnly={readOnly}
                label="Código de retiro courier"
                clickToEdit={order.orderType !== 'reversal'}
              />
            </section>
          ) : (
            <section id={'delivery-codes'}>
              {order.dropCode && (
                <Fragment>
                  <h3>Código de entrega (courier)</h3>
                  {order.status === 'reserved' && (
                    <p className={css.courierCodeHelp}>
                      Para entregar la orden, autentíquese primero desde el botón «Login», ubicado en la esquina inferior
                      derecha del locker, e ingrese el token de su cuenta. Luego, ingrese el código de entrega. Puede
                      consultar su token en{' '}
                      <Link className={css.courierHelpLink} inline to={routes('settings')}>
                        Mi cuenta
                      </Link>.
                    </p>
                  )}
                  <Radio
                    name="dropCodeType"
                    label="Formato de código"
                    value={dropCodeType}
                    style={{ '--radio-accent': 'var(--color-text-light)' }}
                    onChange={(_, value) => setDropCodeType(value)}
                    options={[
                      { value: 'barcode', label: 'Código de barras' },
                      { value: 'qrcode', label: 'Código QR' },
                    ]}
                  />
                  <div className={css.row}>
                    <Barcode
                      className={css.barcode}
                      bottomLabel={
                        <>
                          {clientPreferences.printOrderParams &&
                            clientPreferences.printOrderParams
                              .split(',')
                              .map((param) => ({ name: param.trim(), value: getProperty({ order }, param.trim()) }))
                              // Show all params, even if value is null or blank
                              .map((prop, key) => (
                                <div key={key}>
                                  {orderParams(prop.name.split('.').pop())}: <strong>{prop.value !== undefined ? prop.value : '-'}</strong>
                                </div>
                              ))}
                        </>
                      }
                      ref={barcodeRef}
                      code={order.dropCode}
                      bcid={dropCodeType}
                    />
                    <ReactToPrint
                      trigger={() => (
                        <div style={{ textAlign: 'center' }}>
                          <Button className={css.blueActionButton}>Imprimir código</Button>
                          <div>
                            <small>(etiqueta 10 x 5 cm)</small>
                          </div>
                        </div>
                      )}
                      content={() => barcodeRef.current}
                    />
                  </div>
                </Fragment>
              )}
              <h3>Código de retiro (cliente)</h3>
              <p>
                <small>
                  El código de retiro se envía directamente al destinatario via email una vez que el bulto es entregado
                  en el locker.
                </small>
                {clientPreferences.displayPickupCode && (
                  <Button small onClick={togglePickupCode}>
                    {state.showPickupCode ? 'Ocultar' : 'Mostrar'} código de retiro
                  </Button>
                )}
                {state.showPickupCode && <b>{order.pickupCode}</b>}
              </p>
           
              <h3>Código de revocación (courier)</h3>
              {form.getValue('courierPickupCode') ? (
                <div className={css.row}>
                  <Barcode className={css.barcode} ref={barcodeRef} code={form.getValue('courierPickupCode')} />
                  <ReactToPrint
                    trigger={() => (
                      <div style={{ textAlign: 'center' }}>
                        <Button className={css.blueActionButton}>Imprimir código</Button>
                        <div>
                          <small>(etiqueta 10 x 5 cm)</small>
                        </div>
                      </div>
                    )}
                    content={() => barcodeRef.current}
                  />
                </div>
              ) : (
                <p>
                  <small>
                    {order.orderType == 'reversal' && order.status == 'ready'
                      ? 'Esta orden puede ser retirada por cualquier courier autorizado.'
                      : 'Una vez que sea revocada, la orden podrá ser retirada sin código por cualquier courier autorizado'}
                  </small>
                </p>
              )}
              <Input
                name="courierPickupCode"
                form={form}
                beforeChange={(v) => v.replace(codeInputRegex, '')}
                placeholder="Sin código"
                readOnly={readOnly}
                label="Código de retiro courier"
                clickToEdit={order.orderType !== 'reversal'}
              />
            </section>
          )}
          </div>

          <div className={css.formPanel}>
            <h3 className={css.panelTitle}>Info</h3>
            <OrderInfo order={order} />
            <OrderActivity activity={activity} order={order} clientPreferences={clientPreferences} />
            <Button onClick={actions.history} loading={item.loading && item.action == 'load-history'}>
              {item.loading && item.action == 'load-history' ? null : <FiRefreshCw />}
              &nbsp; Refrescar
            </Button>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Order;
